import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { getRequest as get, putRequest as put } from 'api/utils/HttpMethods';
import {
  createEntity,
  hasSiteID,
  PaginatedResult,
  PaginationParams,
  useGetQuery,
  useMutationQuery,
  usePaginatedQuery,
} from 'api/utils/queries';
import queryString from 'api/utils/queryStringBuilder';

import { isDebit, Debit } from 'interfaces';

export const debitsQueryKeys = {
  all: ['debits'],
  paginatedDebitsByProvider: (
    providerID: string,
    page: number,
    pageSize: number
  ) => [...debitsQueryKeys.all, providerID, page, pageSize],
  paginatedDebitsByUser: (
    userID: string,
    siteID: string | string[],
    page: number,
    pageSize: number,
    globalFilter: string
  ) => [...debitsQueryKeys.all, userID, siteID, page, pageSize, globalFilter],
  paginatedDebitsByPatient: (
    patientID: string,
    page: number,
    pageSize: number
  ) => [...debitsQueryKeys.all, patientID, page, pageSize],
  debitSumByProvider: (providerID: string) => [
    ...debitsQueryKeys.all,
    'debitSum',
    providerID,
  ],
  debitCountByProvider: (providerID: string) => [
    ...debitsQueryKeys.all,
    'debitCount',
    providerID,
  ],
  debitsPerSite: (providerID: string, startDate: string, endDate: string) => [
    ...debitsQueryKeys.all,
    'debitsPerSite',
    providerID,
    startDate,
    endDate,
  ],
  debitsByClaim: (claimID: string) => [
    ...debitsQueryKeys.all,
    'debitsByClaim',
    claimID,
  ],
};

/**
 * @description Fetches paginated debits by provider.
 * @param {PaginationParams} params - The pagination parameters.
 * @param {string} params.providerID - The ID of the provider.
 * @param {number} params.page - The page number.
 * @param {number} params.pageSize - The size of the page.
 * @returns {Promise<PaginatedResult<Debit>>} The paginated debits by provider.
 */
const paginatedDebitsByProvider = async ({
  providerID,
  page,
  pageSize,
}: PaginationParams): Promise<PaginatedResult<Debit>> =>
  await get<PaginatedResult<Debit>>(
    'QBilling',
    `/resource/debit/provider${queryString({ providerID, page, pageSize })}`
  );

/**
 * @description Hook to fetch paginated debits by provider.
 * @param {Object} params - The parameters for the request.
 * @param {string} params.providerID - The ID of the provider.
 * @param {number} params.page - The page number.
 * @param {number} params.pageSize - The size of the page.
 * @returns {UseQueryResult<PaginatedResult<Debit>, Error>} The paginated debits by provider.
 */
export const usePaginatedDebitsByProvider = ({
  providerID,
  page,
  pageSize,
}: {
  providerID: string;
  page: number;
  pageSize: number;
}): UseQueryResult<PaginatedResult<Debit>, Error> => {
  return usePaginatedQuery<Debit>({
    queryKey: debitsQueryKeys.paginatedDebitsByProvider(
      providerID,
      page,
      pageSize
    ),
    isEntity: isDebit,
    functionName: 'paginatedDebitsByProvider',
    params: { providerID, page, pageSize },
    fetchFunction: paginatedDebitsByProvider,
    enabled: !!providerID,
  });
};

/**
 * @description Fetches paginated debits for all users associated providers.
 * @param {string} params.userID - The ID of the user.
 * @param {number} params.page - The page number.
 * @param {number} params.pageSize - The size of the page.
 * @returns {Promise<PaginatedResult<Debit>>} The paginated debits for all of the users associated providers.
 */
const paginatedDebitsByUser = async ({
  userID,
  siteID,
  page,
  pageSize,
  globalFilter,
}: PaginationParams): Promise<PaginatedResult<Debit>> =>
  await get<PaginatedResult<Debit>>(
    'QBilling',
    `/resource/debit/user${queryString({
      userID,
      siteID,
      page,
      pageSize,
      globalFilter,
    })}`
  );

/**
 * @description Hook to fetch paginated debits for all users associated providers.
 * @param {Object} params - The parameters for the request.
 * @param {string} params.userID - The ID of the user.
 * @param {number} params.page - The page number.
 * @param {number} params.pageSize - The size of the page.
 * @returns {UseQueryResult<PaginatedResult<Debit>, Error>} The paginated debits for all of the users associated providers.
 */
export const usePaginatedDebitsByUser = ({
  userID,
  siteID,
  page,
  pageSize,
  globalFilter,
}: {
  userID: string;
  siteID: string | string[];
  page: number;
  pageSize: number;
  globalFilter?: string;
}): UseQueryResult<PaginatedResult<Debit>, Error> => {
  return usePaginatedQuery<Debit>({
    queryKey: debitsQueryKeys.paginatedDebitsByUser(
      userID,
      siteID,
      page,
      pageSize,
      globalFilter
    ),
    isEntity: isDebit,
    fetchFunction: paginatedDebitsByUser,
    functionName: 'paginatedDebitsByUser',
    params: { userID, siteID, page, pageSize, globalFilter },
    enabled: !!userID && hasSiteID(siteID),
  });
};

const paginatedDebitsByPatient = async ({
  patientID,
  page,
  pageSize,
}: PaginationParams): Promise<PaginatedResult<Debit>> =>
  await get<PaginatedResult<Debit>>(
    'QBilling',
    `/resource/debit/patient${queryString({ patientID, page, pageSize })}`
  );

export const usePaginatedDebitsByPatient = ({
  patientID,
  page,
  pageSize,
}: {
  patientID: string;
  page: number;
  pageSize: number;
}): UseQueryResult<PaginatedResult<Debit>, Error> => {
  return usePaginatedQuery<Debit>({
    queryKey: debitsQueryKeys.paginatedDebitsByPatient(
      patientID,
      page,
      pageSize
    ),
    isEntity: isDebit,
    fetchFunction: paginatedDebitsByPatient,
    functionName: 'paginatedDebitsByPatient',
    params: { patientID, page, pageSize },
    enabled: !!patientID,
  });
};

const getDebitByClaim = async (claimID: string): Promise<Debit[]> => {
  return await get<Debit[]>(
    'QBilling',
    `/resource/debit/claim${queryString({ claimID })}`
  );
};

export const useGetDebitByClaim = ({
  claimID,
}: {
  claimID: string;
}): UseQueryResult<Debit[], Error> => {
  return useGetQuery<Debit[]>({
    queryKey: debitsQueryKeys.debitsByClaim(claimID),
    functionName: 'getDebitByClaim',
    fetchFunction: getDebitByClaim,
    enabled: !!claimID,
    params: claimID,
    isEntity: (data): data is Debit[] => Array.isArray(data),
  });
};

const createDebit = async (debit: Debit): Promise<Debit> =>
  createEntity<Debit>({
    entity: debit,
    functionName: 'createDebit',
    isEntity: isDebit,
    path: '/resource/debit',
  });

export const useCreateDebit = (): UseMutationResult<Debit, Error> =>
  useMutationQuery<Debit>({
    errorMessage: 'Failed to create debit',
    mutationFn: createDebit,
    successMessage: 'Debit created successfully',
    queryKey: debitsQueryKeys.all,
  });

const updateDebit = async (debit: Debit): Promise<Debit> =>
  await put<Debit>('QBilling', `/resource/debit`, debit);

export const useUpdateDebit = (): UseMutationResult<Debit, Error> =>
  useMutationQuery<Debit>({
    errorMessage: 'Failed to update debit',
    mutationFn: updateDebit,
    successMessage: 'Debit updated successfully',
    queryKey: debitsQueryKeys.all,
  });
